import './style.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { BlockUI } from 'primereact/blockui';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';

import { updateResult as updateCouncilResult } from 'apis/councils.api';
import {
  getReviseLogs,
  requestExplanation,
  updateExplanationRequest,
} from 'apis/projectProposals.api';
import ErrorDialog from 'components/ErrorDialog';
import {
  CurrencyInput,
  DatePicker,
  HugerteEditor,
  FileInput,
  Select,
  TextareaInput,
} from 'components/FormControl';
import RadioInput from 'components/FormControl/RadioInput';
import Loading from 'components/Loading';
import { COUNCIL_STATUS, TIMEZONE_VN, TOAST } from 'constant';
import { toast } from 'layout';
import {
  addDaysToDate,
  formatFileName,
  getAcadamicLevelShortHand,
  getEndingTimeOfDate,
  getFilterMembersAreAllowedReview,
  getNameRoleOfMemberInCouncil,
  getOrganizationNameById,
  getNumberMembersPresentAndReviewed,
} from 'utils/func';
import { updateCouncilResultValidationSchema } from 'features/Council/validation';
import DateTimePicker from 'components/FormControl/DateTimePicker';

const formOptions = { resolver: yupResolver(updateCouncilResultValidationSchema) };
export default function EditResultFinanceCouncil({
  organizations,
  status,
  owner,
  generalInfoProjectProposal,
  roles,
  handleDownloadFile,
  council,
  refetchCouncil,
  isCouncilLoading,
  onChangeCouncilId,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectProposalId, councilTypeId, councilId } = useParams();

  const commentFilesRef = useRef(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

  const {
    mutate: mutateUpdateCouncilResult,
    isLoading: isUpdateCouncilResultLoading,
    error: updateCouncilResultError,
    isError: isUpdateCouncilResultError,
  } = useMutation(updateCouncilResult);

  const {
    data: explanationRequestsData,
    isLoading: isExplanationRequestsLoading,
    isError: isExplanationRequestsError,
    error: explanationRequestsError,
    refetch: refetchExplanationRequests,
  } = useQuery(
    [
      'projectProposalReviseLogs',
      projectProposalId,
      council?.councilType?.code,
      'ALL_REQUEST_EXPLANATION_FINANCE',
    ],
    () =>
      getReviseLogs({
        projectProposalId,
        types: ['REQUEST_EXPLANATION_FINANCE'],
      }),
    { enabled: !!projectProposalId && !!council?.councilType?.code }
  );

  const explanationRequest = useMemo(() => {
    if (explanationRequestsData?.data?.length > 0) {
      return explanationRequestsData?.data[0];
    }
    return null;
  }, [explanationRequestsData]);

  const {
    mutate: mutateUpdateExplanationRequest,
    isLoading: isUpdateExplanationRequestLoading,
    isError: isUpdateExplanationRequestError,
    error: updateExplanationRequestError,
  } = useMutation(updateExplanationRequest);

  const {
    mutate: mutateRequestExplanation,
    isLoading: isRequestExplanationLoading,
    isError: isRequestExplanationError,
    error: requestExplanationError,
  } = useMutation(requestExplanation);

  const { handleSubmit, control, setValue, reset, watch, trigger } = useForm({
    mode: 'onChange',
    ...formOptions,
  });
  const { isDirty, dirtyFields, errors } = useFormState({
    control,
  });

  const councilStatus = watch('status');
  const oldCommentFileUrls = watch('oldCommentFileUrls');
  const deadline = watch('deadline');
  const isOpened = watch('isOpened');

  const isSendMailCouncilResult = useMemo(
    () =>
      dirtyFields?.status === true &&
      councilStatus !== council?.projectProposalCouncils?.[0]?.councilStatus &&
      councilStatus !== COUNCIL_STATUS.NOT_REVIEWED,
    [dirtyFields?.status, councilStatus]
  );
  const members = useMemo(() => council?.councilMembers, [council]);
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);

  const [minutesFileUrl, setMinutesFileUrl] = useState('');

  const getPageMargins = () => '@page { margin: 0.5in 0.5in 0.2in 0.2in  !important; }';

  // #endregion
  // eslint-disable-next-line no-shadow
  const getNumberMembersReviewed = () =>
    getNumberMembersPresentAndReviewed(council?.councilMembers); // #region Events
  // Set value into form fields
  useEffect(() => {
    setValue('establishDate', council?.timeCreated ? new Date(council?.timeCreated) : '');
    setValue(
      'meetingDate',
      council?.projectProposalCouncils?.[0]?.timeMet ?? council?.timeMet
        ? new Date(council?.projectProposalCouncils?.[0]?.timeMet ?? council?.timeMet)
        : ''
    );
    setValue(
      'reviewDeadline',
      council?.projectProposalCouncils?.[0]?.timesSheduledMeeting
        ? new Date(council?.projectProposalCouncils?.[0]?.timesSheduledMeeting)
        : ''
    );
    setValue('status', council?.projectProposalCouncils?.[0]?.councilStatus);
    setValue('conclusion', council?.projectProposalCouncils?.[0]?.conclusion);
    setValue('approvedBudget', council?.projectProposalCouncils?.[0]?.approvedBudget);
    setMinutesFileUrl(council?.projectProposalCouncils?.[0]?.reportFileUrl);
    setValue(
      'numberMembersAllowReview',
      getFilterMembersAreAllowedReview(council?.councilMembers)?.length ?? 1
    );

    // explanation request
    setValue('comment', explanationRequest?.comment ?? null);
    setValue('oldCommentFileUrls', explanationRequest?.commentFileUrls ?? []);
    setValue('commentFiles', null);
    setValue(
      'deadline',
      explanationRequest?.deadline ? new Date(explanationRequest.deadline) : null
    );
    setValue('isOpened', explanationRequest?.isOpened ?? false);
    setValue('isEditAllowed', explanationRequest?.isEditAllowed ?? false);
    setValue('isShowReview', council?.isShowReview ?? false);
    setValue('isShowReport', council?.isShowReport ?? false);
  }, [council, explanationRequest]);

  const onSubmit = async (data) => {
    const formData = new FormData();

    if (data?.meetingDate) {
      formData.append('timeMet', new Date(data.meetingDate));
    }
    if (data?.minutes) formData.append('reportFile', data.minutes);
    formData.append('projectProposalId', projectProposalId);
    formData.append('councilTypeId', councilTypeId);
    formData.append('councilId', councilId);
    formData.append('councilStatus', data.status);

    if (!members || members?.length === 0) {
      formData.append('noMemberPreQualificationCouncil', true);
    }

    if (data?.approvedBudget) {
      formData.append('approvedBudget', data.approvedBudget);
    }
    if (data?.conclusion) {
      formData.append('conclusion', data?.conclusion);
    }

    const explanationDeadline = getEndingTimeOfDate(
      deadline ? new Date(deadline) : addDaysToDate(new Date(), 7)
    );
    formData.append('deadline', explanationDeadline);

    formData.append('isShowReview', data?.isShowReview);
    formData.append('isShowReport', data?.isShowReport);

    mutateUpdateCouncilResult(formData, {
      onSuccess: ({ data: result }) => {
        if (
          (dirtyFields.isOpened ||
            dirtyFields.comment ||
            (dirtyFields.commentFiles && data?.commentFiles?.length > 0) ||
            dirtyFields.oldCommentFileUrls ||
            dirtyFields.deadline ||
            (councilStatus === COUNCIL_STATUS.QUALIFIED && !deadline) ||
            dirtyFields.isEditAllowed) &&
          councilStatus !== COUNCIL_STATUS.NOT_REVIEWED
        ) {
          const requestFormData = new FormData();

          requestFormData.append('projectProposalId', projectProposalId);
          if (explanationRequest?.id) {
            requestFormData.append('id', explanationRequest?.id);
          }

          if (data?.commentFiles?.length > 0) {
            data?.commentFiles?.forEach((file) => {
              requestFormData.append('commentFiles', file);
            });
          }

          requestFormData.append('comment', data?.comment ?? '');

          if (oldCommentFileUrls?.length > 0) {
            requestFormData.append('oldCommentFileUrls', JSON.stringify(oldCommentFileUrls ?? []));
          }

          requestFormData.append('deadline', explanationDeadline);

          requestFormData.append('isOpened', isOpened ? 'true' : 'false');
          requestFormData.append('isEditAllowed', data?.isEditAllowed ? 'true' : 'false');

          // if isSendMailAllowed = false => not send email request explanation
          // else: check some more conditions to send email request explanation
          requestFormData.append('isSendMailAllowed', 'false');

          const mutate = explanationRequest?.id
            ? mutateUpdateExplanationRequest
            : mutateRequestExplanation;

          if (!explanationRequest?.id) {
            requestFormData.append('type', 'REQUEST_EXPLANATION_FINANCE');
          }

          mutate(requestFormData, {
            onSuccess: () => {
              refetchExplanationRequests();

              commentFilesRef.current.clear();
              setConfirmDialogVisible(false);
              reset({}, { keepValues: true });

              toast(TOAST.SUCCESS, t('council.edit.successResult'));
            },
            onError: () => {
              setIsVisibleDialogError(true);
            },
          });
        } else {
          setConfirmDialogVisible(false);
          reset({}, { keepValues: true });

          toast(TOAST.SUCCESS, t('council.edit.successResult'));
        }

        if (councilId !== result.councilId) {
          onChangeCouncilId(result.councilId);
        }

        refetchCouncil();
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const checkValidation = async () => {
    const isValid = await trigger();
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    } else if (isSendMailCouncilResult) {
      setConfirmDialogVisible(true);
    }
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem mr-3"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );
  // #endregion

  return (
    <>
      {(isCouncilLoading || isExplanationRequestsLoading) && <Loading />}

      {isUpdateCouncilResultError && (
        <ErrorDialog
          title={t('council.edit.titleEditCouncilError')}
          isError={isUpdateCouncilResultError}
          error={updateCouncilResultError}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      {isExplanationRequestsError && (
        <ErrorDialog
          title={t('projectProposal.requestExplanation.errorGetExplanationRequestTitle')}
          isError={isExplanationRequestsError}
          error={explanationRequestsError}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      {isRequestExplanationError && (
        <ErrorDialog
          title={t('projectProposal.requestExplanation.errorRequestExplanationTitle')}
          isError={isRequestExplanationError}
          error={requestExplanationError}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      {isUpdateExplanationRequestError && (
        <ErrorDialog
          title={t('projectProposal.updateExplanationRequest.errorUpdateExplanationRequestTitle')}
          isError={isUpdateExplanationRequestError}
          error={updateExplanationRequestError}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}

      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>

      <BlockUI blocked={isCouncilLoading || isExplanationRequestsLoading}>
        <div className="card relative detail-info-project-proposal">
          <style>{getPageMargins()}</style>

          <h3 className="mb-5 line-height-3 text-4xl text-center font-bold">
            {t('council.viewList.editResult')} {t(`council.viewList.${council?.councilType?.code}`)}
          </h3>

          <h4>{t('projectProposal.general.viewLabelCouncil')}</h4>
          <div className="ml-5 text-lg grid">
            <div className="col-12">
              <span className="font-semibold">
                {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
              </span>
              <span>{generalInfoProjectProposal?.vietnameseName}</span>
            </div>
            <div className="col-12">
              <span className="font-semibold">
                {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
              </span>
              <span>{generalInfoProjectProposal?.englishName}</span>
            </div>
            <div className="col-12">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
              </span>
              <span>
                {`${getAcadamicLevelShortHand(
                  owner?.academicRank,
                  owner?.academicDegree,
                  t,
                  true
                )}${owner?.fullname ?? generalInfoProjectProposal?.owner?.fullname}`}
              </span>
            </div>
          </div>

          <h4>{t(`council.edit.labelResult.${council?.councilType?.code}`)}</h4>
          <div className="ml-5 text-lg grid">
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-bold">
                {t(`council.fields.name.${council?.councilType?.code}`)}:{' '}
              </span>
              <span style={{ fontSize: '16px' }}>{council?.name}</span>
            </div>
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-bold">{t('council.fields.establishDate')}: </span>
              <span>
                {council?.timeCreated &&
                  new Date(council?.timeCreated).toLocaleDateString('en-GB', {
                    timeZone: TIMEZONE_VN,
                  })}
              </span>
            </div>
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
              <span>
                {council?.projectProposalCouncils?.[0]?.reviewDeadline &&
                  new Date(
                    council?.projectProposalCouncils?.[0]?.reviewDeadline
                  ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
              </span>
            </div>
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-bold">{t('council.fields.decisionToEstablish')}: </span>
              {council?.projectProposalCouncils?.[0]?.establishmentFileUrl && (
                <span
                  className="p-link text-primary underline"
                  onClick={
                    () =>
                      handleDownloadFile(
                        council.projectProposalCouncils[0].establishmentFileUrl,
                        councilId
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                >
                  {t('council.view.decision')}
                </span>
              )}
            </div>
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.numberOfMembersReviewed')}: </span>
              <span>
                {getNumberMembersReviewed()}/
                {getFilterMembersAreAllowedReview(council?.councilMembers)?.length}
              </span>
            </div>
          </div>
          <div className="form-table ml-5 mt-3">
            {council?.councilMembers > 0 && (
              <>
                <div className="mb-2" style={{ fontWeight: 900, fontSize: '17px' }}>
                  {t('council.edit.memberList')}
                </div>
                <table className="mb-4">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                        {t('formLayout.numericalOrder')}
                      </th>
                      <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                        {`${t('council.member.fields.academicRank')}, ${t(
                          'council.member.fields.academicDegree'
                        )}, ${t('council.member.fields.name')}`}
                      </th>
                      <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                        Email
                      </th>
                      <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                        {t('council.member.fields.organization')}
                      </th>
                      <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                        {t('council.member.fields.role')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {members?.map((member, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          {`${getAcadamicLevelShortHand(
                            member?.contact?.scientificProfile?.academicRank,
                            member?.contact?.scientificProfile?.academicDegree,
                            t,
                            true
                          )}${member?.contact?.fullname}`}
                        </td>
                        <td>{member?.contact?.email}</td>
                        <td>
                          {getOrganizationNameById(
                            member?.contact?.organizationId,
                            organizations,
                            member?.contact?.specificOrganizationName,
                            t
                          )}
                        </td>
                        <td>
                          {getNameRoleOfMemberInCouncil(roles, member?.councilMemberRole?.id)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <form className="p-fluid ml-5" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="relative">
              <div className="flex">
                <div className="w-3 mr-8">
                  <DateTimePicker
                    label={t('council.fields.meetingDate')}
                    isFontWeightLabel
                    name="meetingDate"
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className="w-3 mr-8">
                  <CurrencyInput
                    label={t('council.fields.approvedBudget')}
                    isFontWeightLabel
                    name="approvedBudget"
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className="w-3">
                  <Select
                    label={t('council.fields.result')}
                    name="status"
                    control={control}
                    isFontWeightLabel
                    errors={errors}
                    options={status}
                    isRequired
                    inputField="value"
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 inline-block" style={{ fontWeight: 900 }}>
                  {t('council.edit.conclusion.financeCouncil')}
                </label>
                <p className="text-lg ml-4 my-0">{t('council.edit.opinion')}:</p>
                <p className="text-lg ml-4 my-0 font-bold font-italic">
                  {t('council.edit.opinionDetail')}:
                </p>
                <HugerteEditor
                  name="conclusion"
                  control={control}
                  errors={errors}
                  style={{ height: 'auto', minHeight: '300px' }}
                />
              </div>
              <div className="flex-1 mt-3">
                <FileInput
                  label={t('council.fields.minutes')}
                  name="minutes"
                  isFontWeightLabel
                  setFileUrl={setMinutesFileUrl}
                  accept=".pdf,.docx"
                  acceptOnly="pdfWordOnly"
                  control={control}
                  errors={errors}
                />
                {minutesFileUrl && (
                  <span
                    className="p-button p-button-text p-button-sm mb-3"
                    onClick={() => handleDownloadFile(minutesFileUrl, councilId)}
                  >
                    <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                    <span className="text-blue-500 underline font-italic text-base">
                      {formatFileName(minutesFileUrl)}
                    </span>
                  </span>
                )}
              </div>
              <div className="mb-2 font-semibold">
                <TextareaInput
                  label={t('projectProposal.requestExplanation.comment')}
                  name="comment"
                  control={control}
                  errors={errors}
                  row={5}
                  disabled={councilStatus === COUNCIL_STATUS.NOT_REVIEWED}
                />
              </div>
              <div className="w-full mb-2">
                <label htmlFor="commentFiles" className="font-semibold">
                  {t('projectProposal.requestUpdate.commentFile')}{' '}
                </label>
                {oldCommentFileUrls?.map((fileUrl, index) => (
                  <div key={fileUrl?.id ?? index} className="flex justify-content-between m-1 p-1">
                    <span
                      className="text-blue-500 underline font-italic text-base cursor-pointer"
                      onClick={() => handleDownloadFile(fileUrl?.name, councilId)}
                    >
                      {formatFileName(fileUrl?.name)}
                    </span>
                    {/*  eslint-disable indent */}
                    {councilStatus !== COUNCIL_STATUS.NOT_REVIEWED && (
                      <i
                        className="pi pi-times text-red-500 text-xl ml-2"
                        onClick={() => {
                          const fileUrls = [...oldCommentFileUrls];
                          fileUrls.splice(index, 1);
                          setValue('oldCommentFileUrls', fileUrls, { shouldDirty: true });
                        }}
                      />
                    )}
                  </div>
                ))}

                <FileInput
                  innerRef={commentFilesRef}
                  name="commentFiles"
                  control={control}
                  errors={errors}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                  acceptOnly="pdfWordExcelOnly"
                  multiple
                  disabled={councilStatus === COUNCIL_STATUS.NOT_REVIEWED}
                />
              </div>
              <div className="w-3 mb-2 font-semibold">
                <DatePicker
                  label={t('projectProposal.requestExplanation.deadline')}
                  name="deadline"
                  control={control}
                  errors={errors}
                  disabled={councilStatus === COUNCIL_STATUS.NOT_REVIEWED}
                />
              </div>

              {councilStatus === COUNCIL_STATUS.QUALIFIED &&
                (!explanationRequest?.id ||
                  (explanationRequest?.id &&
                    !explanationRequest?.isOpened &&
                    !explanationRequest?.ownerComment)) && (
                  // eslint-disable-next-line react/jsx-indent
                  <>
                    <label htmlFor="isOpened" className="block mb-2 font-semibold">
                      {t('projectProposal.requestExplanation.projectProposalRequireExplanation')}
                    </label>
                    <div className="w-full mb-2">
                      <RadioInput
                        name="isOpened"
                        control={control}
                        errors={errors}
                        options={[
                          { value: true, name: t('formLayout.yes') },
                          { value: false, name: t('formLayout.no') },
                        ]}
                        inputField="value"
                        defaultValue={false}
                        disabled={councilStatus !== COUNCIL_STATUS.QUALIFIED}
                      />
                    </div>
                  </>
                )}
              {councilStatus === COUNCIL_STATUS.QUALIFIED &&
                (!explanationRequest?.id ||
                  (explanationRequest?.id &&
                    !explanationRequest?.isOpened &&
                    !explanationRequest?.ownerComment)) && (
                  // eslint-disable-next-line react/jsx-indent
                  <>
                    <label htmlFor="isEditAllowed" className="block mb-2 font-semibold">
                      {t('projectProposal.requestExplanation.allowOwnerEditProjectProposal')}
                    </label>
                    <div className="w-full mb-2">
                      <RadioInput
                        name="isEditAllowed"
                        control={control}
                        errors={errors}
                        options={[
                          { value: true, name: t('formLayout.yes') },
                          { value: false, name: t('formLayout.no') },
                        ]}
                        inputField="value"
                        defaultValue={false}
                        disabled={councilStatus !== COUNCIL_STATUS.QUALIFIED || isOpened === false}
                      />
                    </div>
                  </>
                )}
            </div>

            <label htmlFor="isShowReview" className="block mb-2 font-semibold">
              {t('council.fields.isShowReview')}
            </label>
            <div className="w-full mb-2">
              <RadioInput
                name="isShowReview"
                control={control}
                errors={errors}
                options={[
                  { value: true, name: t('formLayout.yes') },
                  { value: false, name: t('formLayout.no') },
                ]}
                inputField="value"
                defaultValue={false}
              />
            </div>

            <label htmlFor="isShowReport" className="block mb-2 font-semibold">
              {t('council.fields.isShowReport')}
            </label>
            <div className="w-full mb-2">
              <RadioInput
                name="isShowReport"
                control={control}
                errors={errors}
                options={[
                  { value: true, name: t('formLayout.yes') },
                  { value: false, name: t('formLayout.no') },
                ]}
                inputField="value"
                defaultValue={false}
              />
            </div>

            <div className="flex flex-wrap justify-content-end mt-2">
              <Button
                label={t('formLayout.action.close')}
                severity="danger"
                type="button"
                onClick={() => navigate(-1)}
                className="mr-2 w-8rem"
              />
              <Button
                label={t('formLayout.action.update')}
                severity="info"
                type={isSendMailCouncilResult ? 'button' : 'submit'}
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
                onClick={checkValidation}
                className="w-8rem"
              />
            </div>

            {/*
              confirm dialog: use this instead of using .requestSubmit() of form,
              avoid browser compatibility error
            */}
            <div
              className={` ${
                confirmDialogVisible ? '' : 'hidden '
              } p-dialog-mask p-dialog-center p-component-overlay p-component-overlay-enter p-dialog-visible p-dialog-resizable p-dialog-draggable`}
              style={{ zIndex: 1000 }}
            >
              <div
                className="p-dialog p-component p-dialog-default p-dialog-enter-done"
                role="dialog"
                aria-modal="true"
                style={{ width: '400px' }}
              >
                {(isUpdateCouncilResultLoading ||
                  isRequestExplanationLoading ||
                  isUpdateExplanationRequestLoading) && <Loading />}

                <div className="p-dialog-header">
                  <div className="p-dialog-title">
                    <p className="text-blue-500">
                      {t('council.sendMailCouncilResult.confirmationTitle')}
                    </p>
                  </div>
                  <div className="p-dialog-header-icons">
                    <Button
                      type="button"
                      className="p-dialog-header-icon p-dialog-header-close p-link"
                      aria-label="Close"
                      onClick={() => setConfirmDialogVisible(false)}
                      disabled={
                        isUpdateCouncilResultLoading ||
                        isRequestExplanationLoading ||
                        isUpdateExplanationRequestLoading
                      }
                    >
                      <span className="p-dialog-header-close-icon pi pi-times" aria-hidden="true" />
                      <span role="presentation" className="p-ink" />
                    </Button>
                  </div>
                </div>
                <div className="p-dialog-content">
                  <p className="text-lg">{t('council.sendMailCouncilResult.infoMessage')}</p>
                  <p className="text-lg font-semibold">
                    {t('council.sendMailCouncilResult.confirmationMessage')}
                  </p>
                </div>
                <div className="p-dialog-footer">
                  <div>
                    <Button
                      type="button"
                      label={t('projectProposal.cancel')}
                      className="w-7rem"
                      severity="danger"
                      onClick={() => setConfirmDialogVisible(false)}
                      autoFocus
                      disabled={
                        isUpdateCouncilResultLoading ||
                        isRequestExplanationLoading ||
                        isUpdateExplanationRequestLoading
                      }
                    />
                    <Button
                      type="submit"
                      label={t('projectProposal.ok')}
                      className="w-7rem"
                      severity="info"
                      disabled={
                        isUpdateCouncilResultLoading ||
                        isRequestExplanationLoading ||
                        isUpdateExplanationRequestLoading
                      }
                    />
                  </div>
                </div>
                <span className="p-resizable-handle" style={{ zIndex: 90 }} />
              </div>
            </div>
          </form>
        </div>
      </BlockUI>
    </>
  );
}

EditResultFinanceCouncil.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  status: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  owner: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  roles: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  generalInfoProjectProposal: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  council: PropTypes.object,
  handleDownloadFile: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  refetchCouncil: PropTypes.func.isRequired,
  isCouncilLoading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  onChangeCouncilId: PropTypes.func,
};

EditResultFinanceCouncil.defaultProps = {
  organizations: [],
  roles: [],
  generalInfoProjectProposal: {},
  council: {},
};
